import AdmZip from 'adm-zip'
import fs from 'fs'
import path from 'path'

import type { RemoteService } from '../../api/console-client'
import { downloadRemoteService, listRemoteServices } from '../../api/console-client'
import {
  HttpError,
  NotLoggedInError,
  NoWorkspaceError,
  SelectionRequiredError,
  TokenExpiredError,
  UnknownServiceError,
} from '../../errors'
import type { AuthGap, StoredAuth } from '../../project/auth'
import { authGap, loadAuth } from '../../project/auth'
import { computeServiceHash } from '../deploy/hash'
import type { OperationOptions } from '../types'
import { emitter } from '../types'
import { declaredDependencies, extractService, isSafeServiceId } from './extract'

export { declaredDependencies, extractService, isSafeServiceId } from './extract'

// Pulling: bring the workspace's services down into this project so they can be edited and deployed back.
//
// The mirror image of deploying, and it has to be, now that a workspace collects services from any number of
// projects: a fresh checkout can connect to a workspace and get whatever is live there, whoever deployed it.
//
// A service is self-contained, so what lands on disk is nearly the whole archive — source, icons, and the
// service's own `package.json`. Only `node_modules` stays behind, because `npm install` rebuilds it; a
// service that declares dependencies is reported as needing one rather than having it run for it.

/**
 * What the local copy of a service is, relative to the deployed one.
 *
 * Decided on `sourceHash`, which covers the service folder and nothing else — so the answer holds wherever
 * the service is checked out, and "same version" and "same code" are the same question.
 */
export type LocalState = 'absent' | 'same' | 'different'

export type PullEvent =
  | { type: 'listed'; services: RemoteService[]; workspace: Workspace }
  | { type: 'selected'; services: RemoteService[] }
  | { type: 'up-to-date'; serviceId: string; sourceHash: string }
  | { type: 'skipped'; serviceId: string; reason: string }
  | { type: 'pulled'; serviceId: string; sourceHash: string; files: number; directory: string }
  | { type: 'install-needed'; serviceId: string; dependencies: string[] }

export interface Workspace {
  workspaceId: string
  workspaceName: string
}

export interface PullOptions extends OperationOptions<PullEvent> {
  /** Service ids to pull. Wins over `all`. */
  serviceIds?: string[]
  /** Pull every service in the workspace. */
  all?: boolean
  /** Overwrite `services/<id>/` when it already exists. Without this, existing folders are left alone. */
  force?: boolean
  onAuthRequired?: (gap: AuthGap) => Promise<void>
  onSelectionRequired?: (available: RemoteService[]) => Promise<{ serviceIds?: string[]; all?: boolean }>
}

export interface PulledService {
  serviceId: string
  serviceName: string
  sourceHash: string
  files: number
  directory: string
  /** What the service declares. Non-empty means `npm install` is needed in its folder before it will run. */
  dependencies: string[]
}

export interface PullResult {
  workspace: Workspace
  serverUrl: string
  /** Services written to disk. */
  services: PulledService[]
  /** Already identical to what is deployed — nothing to download, nothing to write. */
  upToDate: { serviceId: string; serviceName: string; sourceHash: string }[]
  /** Selected but not written: a local copy that differs, without `--force`. */
  skipped: { serviceId: string; reason: string }[]
}

/**
 * How the local `services/<id>/` compares to the deployed service.
 *
 * A folder that is not there is `absent`; anything else is decided on the hash a deploy would produce.
 */
export async function localState(
  projectRoot: string,
  service: RemoteService
): Promise<LocalState> {
  if (!fs.existsSync(path.resolve(projectRoot, 'services', service.serviceId))) {
    return 'absent'
  }

  // A folder we cannot hash (unreadable, or something that is not a service) is treated as different, so the
  // pull refuses to overwrite it rather than assuming it is safe to replace.
  try {
    return await computeServiceHash(projectRoot, service.serviceId) === service.sourceHash ? 'same' : 'different'
  } catch {
    return 'different'
  }
}

async function resolveAuth(projectRoot: string, onAuthRequired?: PullOptions['onAuthRequired']): Promise<StoredAuth> {
  const stored = loadAuth(projectRoot)
  const gap = authGap(stored)

  if (!gap) {
    return stored as StoredAuth
  }

  if (!onAuthRequired) {
    throw gap === 'not-logged-in' ? new NotLoggedInError() : new NoWorkspaceError()
  }

  await onAuthRequired(gap)

  const refreshed = loadAuth(projectRoot)
  const remaining = authGap(refreshed)

  if (remaining) {
    throw remaining === 'not-logged-in' ? new NotLoggedInError() : new NoWorkspaceError()
  }

  return refreshed as StoredAuth
}

export async function pull(options: PullOptions = {}): Promise<PullResult> {
  const projectRoot = options.cwd ?? process.cwd()
  const emit = emitter(options)

  const { serverUrl, workspaceId, workspaceName, deployToken } = await resolveAuth(projectRoot, options.onAuthRequired)
  const credentials = { serverUrl, workspaceId, deployToken }

  const available = await guard(() => listRemoteServices(credentials))

  emit({ type: 'listed', services: available, workspace: { workspaceId, workspaceName } })

  const selected = await resolveSelection(available, options)

  emit({ type: 'selected', services: selected })

  const pulled: PulledService[] = []
  const upToDate: PullResult['upToDate'] = []
  const skipped: { serviceId: string; reason: string }[] = []

  for (const service of selected) {
    // The id decides where this writes, and it came from the server.
    if (!isSafeServiceId(service.serviceId)) {
      const reason = `"${ service.serviceId }" is not a usable folder name`

      skipped.push({ serviceId: service.serviceId, reason })
      emit({ type: 'skipped', serviceId: service.serviceId, reason })

      continue
    }

    const directory = path.resolve(projectRoot, 'services', service.serviceId)
    const state = await localState(projectRoot, service)

    // Identical code needs no download and no write — and `--force` changes nothing, since there is nothing
    // to overwrite it with. Reported rather than silently omitted: "I checked, and it matches" is an answer.
    if (state === 'same') {
      upToDate.push({
        serviceId  : service.serviceId,
        serviceName: service.serviceName,
        sourceHash : service.sourceHash,
      })
      emit({ type: 'up-to-date', serviceId: service.serviceId, sourceHash: service.sourceHash })

      continue
    }

    if (state === 'different' && !options.force) {
      const reason = `services/${ service.serviceId }/ differs from the deployed version`

      skipped.push({ serviceId: service.serviceId, reason })
      emit({ type: 'skipped', serviceId: service.serviceId, reason })

      continue
    }

    const exists = state === 'different'
    const zipBuffer = await guard(() => downloadRemoteService(credentials, service.serviceId))
    const archive = new AdmZip(zipBuffer)

    // --force replaces rather than merges. Extracting over the top would leave behind files the remote
    // version no longer has, and the next deploy would quietly ship them — a version matching nothing.
    if (exists) {
      fs.rmSync(directory, { recursive: true, force: true })
    }

    const files = extractService(archive, directory)
    const dependencies = declaredDependencies(archive)

    emit({ type: 'pulled', serviceId: service.serviceId, sourceHash: service.sourceHash, files, directory })

    if (dependencies.length > 0) {
      emit({ type: 'install-needed', serviceId: service.serviceId, dependencies })
    }

    pulled.push({
      serviceId  : service.serviceId,
      serviceName: service.serviceName,
      sourceHash : service.sourceHash,
      files,
      directory,
      dependencies,
    })
  }

  return { workspace: { workspaceId, workspaceName }, serverUrl, services: pulled, upToDate, skipped }
}

async function resolveSelection(
  available: RemoteService[],
  options: PullOptions
): Promise<RemoteService[]> {
  const ids = available.map(service => service.serviceId)

  const pick = (selection: { serviceIds?: string[]; all?: boolean }): RemoteService[] => {
    if (selection.serviceIds?.length) {
      return selection.serviceIds.map(serviceId => {
        const match = available.find(service => service.serviceId === serviceId)

        if (!match) {
          throw new UnknownServiceError(serviceId, ids)
        }

        return match
      })
    }

    if (selection.all || available.length <= 1) {
      return available
    }

    throw new SelectionRequiredError(ids, 'This workspace has more than one service, so the pull needs to know which.')
  }

  try {
    return pick(options)
  } catch (error) {
    if (!options.onSelectionRequired || (error as { code?: string }).code !== 'FR_SELECTION_REQUIRED') {
      throw error
    }

    return pick(await options.onSelectionRequired(available))
  }
}

/** Turns a rejected token into the same error the deploy path raises, so the advice is one sentence. */
async function guard<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    if (error instanceof HttpError && (error.status === 401 || error.body.includes('CLI_TOKEN_EXPIRED'))) {
      throw new TokenExpiredError()
    }

    throw error
  }
}
